import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Popper from '@mui/material/Popper';
import Fade from '@mui/material/Fade';
import Paper from '@mui/material/Paper';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { ClickAwayListener } from '@mui/base/ClickAwayListener';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useState, useEffect, memo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { updateAssessmentMarkingAnswer } from '../../../../../../store/actions/dataEntryAssessmentMarkingActions';
import { StaticDatePicker } from '@mui/x-date-pickers/StaticDatePicker';
import { StaticTimePicker } from '@mui/x-date-pickers/StaticTimePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { formatDate } from '../../../../../../utils/date';
import './MarkinVariants.css';

dayjs.extend(utc);
dayjs.extend(timezone);

const getInputType = (question, onChangeInputValue, onKeyUp, value) => {
  const { type, questionId, config } = question;
  const timezone = useSelector((state) => state.tenant?.timezone || 'Australia/Victoria');

  switch (type?.value) {
    case 'shortanswer': {
      return (
        <TextField
          id={`short-answer-${questionId}`}
          autoFocus
          name='value'
          value={value || ''}
          label='Value'
          onChange={(evt) => onChangeInputValue(evt.target.value)}
          onKeyUp={(evt) => onKeyUp(evt)}
          inputProps={{
            required: true,
            maxLength: config?.maxLength || 200,
            spellCheck: false
          }}
          style={{ mr: 1, width: '100%' }}
          className='custom-text-field'
          variant='standard'
        />
      );
    }

    case 'longanswer': {
      return (
        <TextField
          id={`long-answer-${questionId}`}
          name='value'
          value={value || ''}
          label='Value'
          onChange={(evt) => onChangeInputValue(evt.target.value)}
          inputProps={{
            required: true,
            maxLength: config?.maxLength || 500,
            spellCheck: false
          }}
          style={{ mr: 1, width: '100%' }}
          className='custom-text-field'
          autoFocus
          multiline
          minRows={3}
          maxRows={12}
        />
      );
    }

    case 'date': {
      return (
        <>
          <div onClick={(event) => event.stopPropagation()}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <StaticDatePicker
                sx={{
                  '.MuiPickersToolbar-root': {
                    color: '#fff',
                    borderRadius: '2px',
                    borderWidth: '1px',
                    borderColor: 'rgb(50, 102, 139)',
                    border: '1px solid',
                    backgroundColor: 'rgb(50, 102, 139)'
                  }
                }}
                className='custom-text-field'
                id={`date-answer-${questionId}`}
                autoOk={true}
                autoFocus={true}
                name='value'
                value={value ? dayjs(value).tz(timezone) : null}
                onChange={(selectedDate) => {
                  const selectedDateWithZeroTime = dayjs(selectedDate).startOf('day'); // Set time to 00:00:00
                  const utcDate = selectedDateWithZeroTime.tz(timezone).utc();

                  onChangeInputValue(utcDate.toISOString(), true);
                }}
                format='dd/MMM/yyyy'
                displayStaticWrapperAs='desktop'
                showToolbar={false}
                timezone={timezone}
              />
            </LocalizationProvider>
          </div>
        </>
      );
    }
    case 'time': {
      return (
        <>
          <div onClick={(event) => event.stopPropagation()}>
            <StaticTimePicker
              id={`time-answer-${questionId}`}
              name='value'
              value={value ? new Date(value) : null}
              placeholder={'00:00 AM/PM'}
              autoComplete='false'
              onChange={(value) => onChangeInputValue(value)}
              onKeyUp={(evt) => onKeyUp(evt)}
              fullWidth
              displayStaticWrapperAs='desktop'
              showActionbar={false}
              autoFocus={true}
              ampm={true}
            />
          </div>
        </>
      );
    }
    default: {
      return <></>;
    }
  }
};

const MarkingInput = memo(({ id, question, record, onChangeAlert, disabled }) => {
  const { answer, questionId, type } = question;
  const [value, setValue] = useState(answer && answer.length ? answer[0] : null);
  const dispatch = useDispatch();
  const [anchorEl, setAnchorEl] = useState(null);
  const [open, setOpen] = useState(false);
  const [placement, setPlacement] = useState();
  const showActions = type?.value !== 'date';
  const timezone = useSelector((state) => state.tenant?.timezone || 'Australia/Victoria');
  const hasValue = answer && answer?.length > 0;

  const handleClick = (newPlacement) => (event) => {
    event.preventDefault();
    setAnchorEl(event?.currentTarget);
    setOpen(true);
    setPlacement(newPlacement);
  };

  const handleClose = (evt) => {
    evt?.stopPropagation();
    setOpen(false);
    setValue(answer && answer.length ? answer[0] : null);
  };

  const onChangeInput = (newValue, mustSave) => {
    if (mustSave) {
      dispatch(
        updateAssessmentMarkingAnswer({
          recordId: record?._id,
          code: record?.code,
          questionId,
          answer: newValue ? [newValue] : []
        })
      );

      onChangeAlert();
      setOpen(false);
    } else {
      setValue(newValue);
    }
  };

  const onKeyUp = (evt) => {
    if (evt?.keyCode === 13 || evt?.key === 'Enter') {
      onSave(evt);
    }
  };

  const inputContent = getInputType(question, onChangeInput, onKeyUp, value);

  const onSave = async (evt) => {
    evt?.stopPropagation();
    evt?.preventDefault();

    dispatch(
      updateAssessmentMarkingAnswer({
        recordId: record?._id,
        code: record?.code,
        questionId,
        answer: value ? [value] : []
      })
    );

    onChangeAlert();

    handleClose();
  };

  const formatTime = (date) => {
    const hours = date.getHours() === 0 ? 12 : String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const meridian = date.getHours() >= 12 ? 'PM' : 'AM';

    return `${hours > 12 ? hours - 12 : hours}:${minutes} ${meridian}`;
  };

  const getAnswerLabel = () => {
    const questionAnswer = answer || [];

    if (type?.value === 'date' && questionAnswer.length) {
      return formatDate(new Date(questionAnswer[0]), timezone, 'DD/MMM/YYYY');
    }

    if (type?.value === 'time' && questionAnswer.length) {
      return formatTime(new Date(questionAnswer[0]));
    }

    //TODO: Format date and time here..

    return questionAnswer.join(' ');
  };

  const onCancel = (evt) => {
    evt.stopPropagation();
    evt.preventDefault();
    const prevValue = answer && answer.length ? answer[0] : '';

    setValue(prevValue);

    handleClose();
  };

  const handleClearValue = useCallback(() => {
    setValue([]);
    dispatch(
      updateAssessmentMarkingAnswer({
        recordId: record?._id,
        code: record?.code,
        questionId,
        answer: []
      })
    );
  }, []);

  useEffect(() => {
    setValue(answer && answer.length ? answer[0] : null);
  }, [answer]);

  return (
    <ClickAwayListener onClickAway={handleClose}>
      <div
        id={id}
        aria-describedby={id}
        variant='contained'
        onClick={disabled ? undefined : handleClick('bottom')}
        style={{
          position: 'relative'
        }}
      >
        <Chip
          style={{ width: 100 }}
          className={`clickeable text-clickeable chip-clickeable ${
            answer && answer.length ? '' : 'bg-fourth-orange'
          } ${open ? 'selected-chip' : ''}  `}
          label={`${getAnswerLabel()}`}
          disabled={disabled}
          {...(hasValue
            ? {
                onDelete: (evt) => {
                  evt.stopPropagation();
                  handleClearValue();
                }
              }
            : {})}
        />
        <Box sx={{ position: 'relative' }}>
          <Popper className='marking-menu' open={open} anchorEl={anchorEl} placement={placement} transition>
            {({ TransitionProps }) => (
              <Fade {...TransitionProps} timeout={100}>
                <Paper>
                  <div className='marking-menu__content' style={{ maxHeight: '100%' }}>
                    <>{inputContent}</>
                    {showActions && (
                      <div
                        className='marking-menu__content__actions'
                        style={{
                          marginLeft: '20px',
                          marginTop: '20px'
                        }}
                      >
                        <Button
                          className='button-cancel'
                          size='small'
                          onClick={(evt) => {
                            onCancel(evt);
                          }}
                          startIcon={
                            <FontAwesomeIcon
                              icon={['fas', 'xmark']}
                              style={{ width: '20px', height: '15px' }}
                            />
                          }
                        />
                        {value && value !== '' ? (
                          <Button
                            className=' button-save'
                            size='small'
                            onClick={(evt) => {
                              onSave(evt);
                            }}
                            startIcon={
                              <FontAwesomeIcon icon='check' style={{ width: '20px', height: '14px' }} />
                            }
                          />
                        ) : (
                          <></>
                        )}
                      </div>
                    )}
                  </div>
                </Paper>
              </Fade>
            )}
          </Popper>
        </Box>
      </div>
    </ClickAwayListener>
  );
});

export default MarkingInput;
